import { Theme } from '@mui/material';

type OptionSurfaceState = {
  selected: boolean;
  /** False for an identity already shared: it cannot be chosen, so it answers no pointer. */
  available: boolean;
};

/**
 * The look an identity card and a "new one" option share: the resting hairline border, and for a
 * chosen option the 2px primary border, a lifted shadow and a plain surface. Both use an inset
 * box shadow for the border so choosing an option never shifts the content inside it.
 *
 * An available option that is not chosen also answers the pointer: the card hover colours under
 * it and the card press colour while pressed, shifting over 120ms (not at all for a person who
 * asked for reduced motion). A chosen option keeps its chosen look, and an unavailable one keeps
 * its resting look.
 *
 * The hover rule is not wrapped in `@media (hover: hover)`. A tap on an available option chooses
 * it, and a chosen option keeps its chosen look, so a hover that sticks after a tap can never
 * show. A headless browser on a Linux runner has no pointing device and answers `(hover: none)`,
 * where a Mac answers `(hover: hover)`, so that wrapper made the hover colour depend on the machine.
 */
export function identityOptionSurface(theme: Theme, { selected, available }: OptionSurfaceState) {
  const answersPointer = available && !selected;
  const { hoverBackground, hoverBorder, activeBackground, stateTransitionDuration } =
    theme.identityCard;
  const pointerResponse = {
    transition: `background-color ${stateTransitionDuration}, box-shadow ${stateTransitionDuration}`,
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
    '&:hover': {
      backgroundColor: hoverBackground,
      boxShadow: `inset 0 0 0 ${theme.semantic.spacing['4xs']} ${hoverBorder}`,
    },
    '&:active': { backgroundColor: activeBackground },
  };

  return {
    position: 'relative' as const,
    boxSizing: 'border-box' as const,
    borderRadius: theme.semantic.borderRadius.lg,
    backgroundColor: selected ? theme.palette.background.paper : theme.palette.background.default,
    boxShadow: selected
      ? `inset 0 0 0 ${theme.semantic.spacing['3xs']} ${theme.palette.primary.main}, ${theme.shadows[4]}`
      : `inset 0 0 0 ${theme.semantic.spacing['4xs']} ${theme.palette.divider}`,
    ...(answersPointer ? pointerResponse : {}),
    '&.Mui-focusVisible': {
      outline: `${theme.semantic.spacing['3xs']} solid ${theme.palette.primary.main}`,
      outlineOffset: theme.semantic.spacing['3xs'],
    },
  };
}
